import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import type { TaskDetail, BoardStatus, BoardLabel, BoardMember } from "./task-types";

interface UseTaskDetailDataParams {
  taskId: string | null;
  boardIdOverride?: string;
  enabled?: boolean;
  onArchiveSuccess?: () => void;
}

export function useTaskDetailData({
  taskId,
  boardIdOverride,
  enabled = true,
  onArchiveSuccess,
}: UseTaskDetailDataParams) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const isEditingRef = useRef(false);
  isEditingRef.current = isEditing;
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStatusId, setEditStatusId] = useState("");
  const [editAssignee, setEditAssignee] = useState<string | null>(null);
  const [editDueDate, setEditDueDate] = useState<Date | undefined>(undefined);
  const [editPriority, setEditPriority] = useState<string | null>(null);
  const [newAnimalSearch, setNewAnimalSearch] = useState("");
  const [newInteractionSearch, setNewInteractionSearch] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const { data: taskData, isLoading } = useQuery<{ task: TaskDetail }>({
    queryKey: ["/api/tasks", taskId],
    enabled: !!taskId && enabled,
  });

  const task = taskData?.task;
  const boardId = boardIdOverride || task?.boardId || "";

  const { data: statusesData } = useQuery<{ statuses: BoardStatus[] }>({
    queryKey: ["/api/task-boards", boardId, "statuses"],
    enabled: !!boardId && enabled,
  });

  const { data: labelsData } = useQuery<{ labels: BoardLabel[] }>({
    queryKey: ["/api/task-boards", boardId, "labels"],
    enabled: !!boardId && enabled,
  });

  const { data: membersData } = useQuery<{ members: BoardMember[] }>({
    queryKey: ["/api/tasks/board-members", boardId],
    enabled: !!boardId && enabled,
  });

  const { data: animalSearchData } = useQuery<{ animals: Array<{ animalId: string; name: string; species: string | null; primaryBreed: string | null; location: string | null; zoneCode: string | null }> }>({
    queryKey: ["/api/tasks/animal-search?q=" + encodeURIComponent(newAnimalSearch)],
    enabled: newAnimalSearch.length >= 2 && enabled,
  });

  const { data: interactionSearchData } = useQuery<{ interactions: Array<{ id: string; animalId: string; eventType: string; occurredAt: string; author: string | null; scoreCode: string | null; locationCode: string | null; animalName: string | null; animalSpecies: string | null }> }>({
    queryKey: ["/api/tasks/interaction-search?q=" + encodeURIComponent(newInteractionSearch)],
    enabled: newInteractionSearch.length >= 2 && enabled,
  });

  const activeStatuses = (statusesData?.statuses ?? []).filter(s => !s.isArchived);
  const allLabels = (labelsData?.labels ?? []).filter(l => !l.isArchived);
  const members = membersData?.members ?? [];

  const userRole = (() => {
    if (user?.isAdmin) return "admin";
    const member = members.find(m => m.userId === user?.id);
    return member?.role ?? null;
  })();
  const canEdit = userRole === "admin" || userRole === "editor";
  const canComment = !!userRole;

  useEffect(() => {
    if (task && !isEditingRef.current) {
      setEditTitle(task.title);
      setEditDescription(task.description ?? "");
      setEditStatusId(task.statusId);
      setEditAssignee(task.assigneeUserId);
      setEditDueDate(task.dueDate ? new Date(task.dueDate + "T00:00:00") : undefined);
      setEditPriority(task.priority);
    }
  }, [task]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => apiRequest("PATCH", `/api/tasks/${taskId}`, data),
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Task updated" });
      setIsEditing(false);
    },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const isArchived = task?.archivedAt !== null && task?.archivedAt !== undefined;

  const archiveMutation = useMutation({
    mutationFn: async () => apiRequest("PATCH", `/api/tasks/${taskId}/archive`),
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: isArchived ? "Task unarchived" : "Task archived" });
      if (!isArchived) onArchiveSuccess?.();
    },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  function handleSave() {
    if (!task) return;
    const updates: Record<string, any> = {};
    if (editTitle !== task.title) updates.title = editTitle;
    if (editDescription !== (task.description ?? "")) updates.description = editDescription || null;
    if (editStatusId !== task.statusId) updates.statusId = editStatusId;
    if (editAssignee !== task.assigneeUserId) updates.assigneeUserId = editAssignee;
    const dueDateStr = editDueDate ? editDueDate.toISOString().split("T")[0] : null;
    if (dueDateStr !== task.dueDate) updates.dueDate = dueDateStr;
    if (editPriority !== task.priority) updates.priority = editPriority;
    if (Object.keys(updates).length > 0) updateMutation.mutate(updates);
    else setIsEditing(false);
  }

  const assignedLabelIds = new Set(task?.labels.map(l => l.labelId) ?? []);
  const availableLabels = allLabels.filter(l => !assignedLabelIds.has(l.id));
  const isOverdue = task?.dueDate ? new Date(task.dueDate) < new Date(new Date().toISOString().split("T")[0]) : false;

  return {
    task,
    isLoading,
    boardId,
    members,
    activeStatuses,
    allLabels,
    availableLabels,
    assignedLabelIds,
    isOverdue,
    canEdit,
    canComment,
    userRole,
    currentUserId: user?.id ?? "",

    isEditing,
    setIsEditing,
    editTitle,
    setEditTitle,
    editDescription,
    setEditDescription,
    editStatusId,
    setEditStatusId,
    editAssignee,
    setEditAssignee,
    editDueDate,
    setEditDueDate,
    editPriority,
    setEditPriority,
    datePickerOpen,
    setDatePickerOpen,

    newAnimalSearch,
    setNewAnimalSearch,
    newInteractionSearch,
    setNewInteractionSearch,
    animalSearchResults: animalSearchData?.animals,
    interactionSearchResults: interactionSearchData?.interactions,

    updateMutation,
    archiveMutation,
    isArchived,
    handleSave,
  };
}
